/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import Breadcrumbs from "components/common/Breadcrumbs";
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getOrganizationType } from "lib/organizationType";
import { useEffect, useState } from "react";
import useSearchParamsOperations from "hooks/useSearchParamsOperations";
import usePickups from "hooks/pickups/usePickups";
import useAssets from "hooks/assets/useAssets";
import { AssetsSearchField } from "./AssetsSearchField";
import { SecurityLocks } from "./SecurityLocks";
import { AssetsGrid } from "./AssetsDataGrid";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { surfacePanelSx } from "constants/pageSurfaces";

function Assets() {
  const isAssettech = getOrganizationType() === "assettech";
  const location = useLocation();
  const navigate = useNavigate();
  const {
    assets,
    isLoading,
    isError,
    meta,
    refetch,
    setSelectedAssetIds,
    selectedAssets,
    selectedAssetIds,
    bulkUnenroll,
    setSelectAllFlag,
    selectAllFlag,
    getAllSerialsNumbers,
  } = useAssets();
  const [searchParams] = useSearchParams();
  const {
    errorMessage,
    isLoading: filterIsLoading,
    hasNextPage,
    incrementCurrentPage,
  } = usePickups(true);
  const {
    replace,
    deleteFieldInArrayUsingPrefix,
    replaceInArrayFieldUsingPrefix,
    commitListSearch,
    commitListSearchSync,
    cancelCommitListSearch,
  } = useSearchParamsOperations();
  const [pickupIds] = useState(
    searchParams
      .getAll("filter[]")
      .find((p) => p.includes("pickup_id,is_any_of"))
      ?.split("pickup_id,is_any_of,")[1]
      ?.split(",") || []
  );
  const [locationId] = useState(
    searchParams
      .getAll("filter[]")
      .find((p) => p.includes("location_id,is"))
      ?.split("location_id,is,")[1] || ""
  );
  const [status] = useState(
    searchParams
      .getAll("filter[]")
      .find((p) => p.includes("status,is"))
      ?.split("status,is,")[1] || ""
  );
  const [value, setValue] = useState<"active" | "retired">(() =>
    getOrganizationType() === "assettech" ? "active" : "retired"
  );
  const [securityLocksOpen, setSecurityLocksOpen] = useState(false);
  const [, setRetiredAssetOpen] = useState(false);

  useEffect(() => {
    const st = location.state as { refreshAssets?: boolean } | null;
    if (st?.refreshAssets) {
      refetch();
      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, refetch, navigate]);

  useInfiniteScroll({
    loading: filterIsLoading,
    hasNextPage,
    onLoadMore: incrementCurrentPage,
    disabled: !!errorMessage,
    rootMargin: "0px 0px 400px 0px",
  });

  useEffect(() => {
    replaceInArrayFieldUsingPrefix(
      "filter[]",
      "state,is_any_of,",
      `state,is_any_of,${value}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (pickupIds && pickupIds.length > 0) {
      replaceInArrayFieldUsingPrefix(
        "filter[]",
        "pickup_id,is_any_of",
        `pickup_id,is_any_of,${pickupIds.join(",")}`
      );
      if (value !== "retired") setValue("retired");
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "pickup_id,is_any_of");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupIds]);

  useEffect(() => {
    if (locationId && locationId.length > 0) {
      replaceInArrayFieldUsingPrefix(
        "filter[]",
        "location_id,is",
        `location_id,is,${locationId}`
      );
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "location_id,is");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  useEffect(() => {
    if (status && status.length > 0) {
      replaceInArrayFieldUsingPrefix(
        "filter[]",
        "status,is",
        `status,is,${status}`
      );
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "status,is");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <div>
      <Box>
        <Breadcrumbs
          data={[
            ...(isAssettech
              ? [{ label: "Dashboard", to: "/dashboard" }]
              : [{ label: "Home", to: "/" }]),
            { label: "Assets" },
          ]}
        ></Breadcrumbs>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
          mt: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            flexGrow: 1,
          }}
        >
          <Typography variant="h4" fontWeight="bold">
            Assets
          </Typography>
          <AssetsSearchField
            commitListSearch={commitListSearch}
            commitListSearchSync={commitListSearchSync}
            cancelCommitListSearch={cancelCommitListSearch}
          />
        </Box>
        {isAssettech ? (
          <Button component={RouterLink} variant="contained" to="/assets/new">
            Register asset
          </Button>
        ) : null}
      </Box>

      <Box
        sx={{
          ...surfacePanelSx,
          p: 2,
          width: "100%",
          maxWidth: "100%",
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Box sx={{ mt: 0, ".MuiTabPanel-root": { padding: 0 } }}>
          {isAssettech ? (
            <ToggleButtonGroup
              exclusive
              value={value}
              onChange={(_, next) => {
                if (next != null) setValue(next);
              }}
              aria-label="Asset registry view"
              sx={{ mb: 2 }}
            >
              <ToggleButton value="active">Live</ToggleButton>
              <ToggleButton value="retired">Retired</ToggleButton>
            </ToggleButtonGroup>
          ) : null}
          {selectedAssets && selectedAssets[0] && (
            <>
              <SecurityLocks
                open={securityLocksOpen}
                setOpen={setSecurityLocksOpen}
                bulkUnenroll={bulkUnenroll}
              />
            </>
          )}
          <AssetsGrid
            fieldsToHide={["assignedTo"]}
            replace={replace}
            assets={assets}
            isLoading={isLoading}
            isError={isError}
            meta={meta}
            refetch={refetch}
            setSelectedAssetIds={setSelectedAssetIds}
            selectedAssetIds={selectedAssetIds}
            setSelectAllFlag={setSelectAllFlag}
            selectAllFlag={selectAllFlag}
            assetsTab={value === "active" ? "live" : "retired"}
            setRetiredAssetOpen={setRetiredAssetOpen}
            setSecurityLocksOpen={setSecurityLocksOpen}
            getAllSerialsNumbers={getAllSerialsNumbers}
          />
        </Box>
      </Box>
    </div>
  );
}

export default Assets;
